import { useCallback, useEffect, useRef, useState } from 'react';
import {
    STATUS_COLOR,
    weatherIconSrc,
    MONITOR_ICON_RI,
    type LatLng,
    type Zone,
} from './demo2Data';

/* ═══════════════════════════════════════════════════════════
 * 실제 Naver Map(NCP) 기반 관리구역 지도.
 * ais_admin_client의 기존 DemoMap.tsx / DemoSiteDetail.tsx 와 동일한
 * 방식(VITE_NCP_API_KEY_ID)으로 Naver Maps 스크립트를 로드하고,
 * 구역 경계는 demoStorage.ts 실측 폴리곤 값을 그대로 Polygon으로 렌더링한다.
 * 기존 /demo(DemoMap.tsx)의 "클릭해서 폴리곤을 그려 신규 지점 등록" 기능과
 * 동일한 방식의 그리기 모드(drawingMode)도 지원한다.
 * ═══════════════════════════════════════════════════════════ */

const labelHtml = (
    zone: Zone,
    color: string,
    selected: boolean,
    showWeather: boolean,
) => {
    const barW = selected ? 64 : 54;
    const pct = Math.max(0, Math.min(100, zone.moist));
    const weatherBadge = showWeather
        ? `
        <div style="position:absolute;left:50%;bottom:100%;margin-bottom:0px;transform:translateX(-50%);
            width:50%;pointer-events:none;">
            <img src="${weatherIconSrc(zone.weather)}" style="display:block;width:100%;height:auto;
                filter:drop-shadow(0 3px 6px rgba(0,0,0,0.4));" />
        </div>`
        : '';
    /* 센서기반(실측) vs AI예측(센서리스) 구분을 지도만 보고도 알 수 있도록 마커 좌상단에 항상 표시.
     * 센서기반은 실시간 송수신 중임을 나타내는 와이파이 핑(ping) 애니메이션 링을 함께 표시한다. */
    const isSensor = zone.monitorType === 'sensor';
    const monitorBadge = `
        <div style="position:absolute;top:-9px;left:-9px;width:22px;height:22px;">
            ${isSensor ? '<span class="d2map-sensor-ping"></span><span class="d2map-sensor-ping d2"></span>' : ''}
            <div style="position:relative;width:100%;height:100%;border-radius:50%;
                background:${isSensor ? '#2563EB' : '#7C3AED'};display:flex;
                align-items:center;justify-content:center;border:2px solid #fff;
                box-shadow:0 2px 5px rgba(0,0,0,0.3);z-index:2;">
                <i class="${MONITOR_ICON_RI[zone.monitorType]}" style="color:#fff;font-size:10.5px;line-height:1;"></i>
            </div>
        </div>`;
    return `
    <div style="position:relative;background:rgba(255,255,255,${selected ? 0.98 : 0.94});border:2px solid ${color};
        border-radius:8px;padding:5px 10px 6px;font-size:${selected ? 11.5 : 10.5}px;font-weight:800;color:#1A2B1A;
        white-space:nowrap;font-family:Pretendard,-apple-system,sans-serif;
        box-shadow:0 2px 10px rgba(0,0,0,${selected ? 0.22 : 0.13});line-height:1.6;cursor:pointer;
        transform:scale(${selected ? 1.08 : 1});transition:transform .15s;">
        ${weatherBadge}
        ${monitorBadge}
        <div style="display:flex;align-items:center;gap:6px;">
            <span>${zone.icon}</span><span>${zone.name}</span>
        </div>
        <div style="display:flex;align-items:center;gap:6px;margin-top:3px;">
            <div style="width:${barW}px;height:5px;background:#E2E8E2;border-radius:3px;overflow:hidden;flex-shrink:0;">
                <div style="width:${pct}%;height:100%;background:${color};border-radius:3px;"></div>
            </div>
            <span style="color:${color};font-weight:800;font-size:${selected ? 10.5 : 9.5}px;flex-shrink:0;">${zone.moist}%</span>
        </div>
    </div>`;
};

const vtxHtml = (canClose: boolean) => `
    <div style="width:18px;height:18px;background:${canClose ? '#1E8B53' : '#fff'};
        border:3px solid #1E8B53;border-radius:50%;cursor:${canClose ? 'pointer' : 'crosshair'};
        box-shadow:0 2px 8px rgba(30,139,83,0.5);"></div>`;
const midVtxHtml = () => `
    <div style="width:12px;height:12px;background:#fff;border:2.5px solid #1E8B53;
        border-radius:50%;cursor:crosshair;box-shadow:0 1px 4px rgba(0,0,0,0.25);"></div>`;

function pxDist(map: any, nm: any, a: LatLng, b: LatLng): number {
    try {
        const proj = map.getProjection();
        const pa = proj.fromCoordToOffset(new nm.LatLng(a.lat, a.lng));
        const pb = proj.fromCoordToOffset(new nm.LatLng(b.lat, b.lng));
        return Math.sqrt((pa.x - pb.x) ** 2 + (pa.y - pb.y) ** 2);
    } catch {
        return Infinity;
    }
}

interface Props {
    zones: Zone[];
    selectedId?: string | null;
    onSelectZone?: (id: string) => void;
    zoom?: number;
    mapTypeToggle?: boolean;
    /** 최초 표시할 지도 타입 (기본: 위성) */
    defaultMapType?: 'NORMAL' | 'SATELLITE';
    /** 일반/위성 토글 버튼 위치 — 화면 내 다른 오버레이(카드 등)와 겹치지 않도록 선택 */
    toggleAnchor?: 'top-right' | 'bottom-right';
    /** true인 동안 지도를 클릭해 폴리곤(신규 관리구역 경계)을 그릴 수 있다 */
    drawingMode?: boolean;
    /** 꼭지점 3개 이상 찍은 뒤 첫 꼭지점 근처를 다시 클릭하면 완성된 좌표 배열로 호출된다 */
    onDrawingComplete?: (pts: LatLng[]) => void;
    /** 각 구역 마커 우상단에 현재 날씨 아이콘 배지를 표시 */
    showWeather?: boolean;
    /** 지정 시 이 id 목록에 포함된 구역만 화면에 표시(레이어는 유지한 채 숨김/표시만 전환) —
     *  대시보드 지도카드 상태 필터 등에서 사용. 미지정(undefined) 시 전체 표시. */
    visibleIds?: string[];
}

export default function Demo2ZoneMap({
    zones,
    selectedId,
    onSelectZone,
    zoom = 15,
    mapTypeToggle = false,
    defaultMapType = 'SATELLITE',
    toggleAnchor = 'bottom-right',
    drawingMode = false,
    onDrawingComplete,
    showWeather = false,
    visibleIds,
}: Props) {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<any>(null);
    const layersRef = useRef<Record<string, { polygon: any; label: any }>>({});
    const [ready, setReady] = useState(false);
    const [mapType, setMapType] = useState<'NORMAL' | 'SATELLITE'>(
        defaultMapType,
    );
    const onSelectRef = useRef(onSelectZone);
    onSelectRef.current = onSelectZone;
    const onDrawCompleteRef = useRef(onDrawingComplete);
    onDrawCompleteRef.current = onDrawingComplete;

    /* ── 그리기 모드 상태 ── */
    const drawPtsRef = useRef<LatLng[]>([]);
    const drawMkrsRef = useRef<any[]>([]);
    const drawLineRef = useRef<any>(null);
    const drawEvtRef = useRef<any>(null);
    const [ptCount, setPtCount] = useState(0);

    /** 구역 하나의 폴리곤+라벨 마커를 생성해 layersRef에 등록한다 (최초 로드 및 신규 구역 추가 시 공용) */
    const createLayer = useCallback(
        (nm: any, map: any, z: Zone, isSel: boolean, showWx: boolean) => {
            const color = STATUS_COLOR[z.status];
            const polygon = new nm.Polygon({
                map,
                paths: z.polygon.map(p => new nm.LatLng(p.lat, p.lng)),
                fillColor: color,
                fillOpacity: isSel ? 0.32 : 0.2,
                strokeColor: color,
                strokeWeight: isSel ? 3.5 : 2.5,
                strokeOpacity: 0.9,
                zIndex: isSel
                    ? 100
                    : z.status === 'danger'
                      ? 30
                      : z.status === 'warn'
                        ? 20
                        : 10,
                clickable: true,
            });
            const label = new nm.Marker({
                position: new nm.LatLng(z.lat, z.lng),
                map,
                icon: {
                    content: labelHtml(z, color, isSel, showWx),
                    anchor: new nm.Point(50, 28),
                },
                zIndex: (isSel ? 100 : 10) + 1,
                clickable: true,
            });
            nm.Event.addListener(polygon, 'click', () =>
                onSelectRef.current?.(z.id),
            );
            nm.Event.addListener(label, 'click', () =>
                onSelectRef.current?.(z.id),
            );
            layersRef.current[z.id] = { polygon, label };
        },
        [],
    );

    const initMap = useCallback(() => {
        if (!containerRef.current || !window.naver?.maps || mapRef.current)
            return;
        const nm = window.naver.maps;

        const avgLat = zones.reduce((s, z) => s + z.lat, 0) / zones.length;
        const avgLng = zones.reduce((s, z) => s + z.lng, 0) / zones.length;

        const map = new nm.Map(containerRef.current, {
            center: new nm.LatLng(avgLat, avgLng),
            zoom,
            minZoom: 10,
            maxZoom: 20,
            mapTypeId: nm.MapTypeId[defaultMapType],
            scaleControl: false,
            logoControl: true,
            mapDataControl: false,
            zoomControl: true,
            zoomControlOptions: {
                style: nm.ZoomControlStyle.SMALL,
                position: nm.Position.TOP_LEFT,
            },
        });
        mapRef.current = map;
        /* 지적편집도(폴리곤) 레이어 — 위성/일반 지도 어느 쪽이든 함께 표시 */
        try {
            new nm.CadastralLayer().setMap(map);
        } catch {
            /* 지적편집도 레이어 미지원 시 무시 */
        }

        zones.forEach(z =>
            createLayer(nm, map, z, z.id === selectedId, showWeather),
        );

        setReady(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const key = import.meta.env.VITE_NCP_API_KEY_ID ?? '';
        if (window.naver?.maps) {
            initMap();
            return;
        }
        const existing = document.querySelector<HTMLScriptElement>(
            'script[src*="openapi.map.naver.com"]',
        );
        if (existing) {
            existing.addEventListener('load', () => setTimeout(initMap, 100));
        } else {
            const script = document.createElement('script');
            script.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${key}&submodules=geocoder,geometry`;
            script.async = true;
            script.onload = () => setTimeout(initMap, 100);
            document.head.appendChild(script);
        }
        return () => {
            Object.values(layersRef.current).forEach(({ polygon, label }) => {
                polygon?.setMap(null);
                label?.setMap(null);
            });
            layersRef.current = {};
            mapRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* 선택된 구역이 바뀌면 폴리곤/라벨 스타일 갱신 + 지도 이동 */
    useEffect(() => {
        if (!ready || !window.naver?.maps || !mapRef.current) return;
        const nm = window.naver.maps;
        zones.forEach(z => {
            const layer = layersRef.current[z.id];
            if (!layer) return;
            const isSel = z.id === selectedId;
            const color = STATUS_COLOR[z.status];
            layer.polygon.setOptions({
                fillOpacity: isSel ? 0.32 : 0.2,
                strokeWeight: isSel ? 3.5 : 2.5,
            });
            layer.polygon.setZIndex(
                isSel
                    ? 100
                    : z.status === 'danger'
                      ? 30
                      : z.status === 'warn'
                        ? 20
                        : 10,
            );
            layer.label.setIcon({
                content: labelHtml(z, color, isSel, showWeather),
                anchor: new nm.Point(50, 28),
            });
        });
        if (selectedId) {
            const z = zones.find(zz => zz.id === selectedId);
            if (z) mapRef.current.panTo(new nm.LatLng(z.lat, z.lng));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId, ready]);

    /* 지도가 이미 만들어진 뒤 zones 배열에 새 구역이 추가되면(신규 등록 등) 해당 폴리곤/라벨만 추가 생성 */
    useEffect(() => {
        if (!ready || !window.naver?.maps || !mapRef.current) return;
        const nm = window.naver.maps;
        zones.forEach(z => {
            if (!layersRef.current[z.id])
                createLayer(
                    nm,
                    mapRef.current,
                    z,
                    z.id === selectedId,
                    showWeather,
                );
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zones, ready]);

    /* 상태 필터 등으로 visibleIds가 주어지면 레이어를 새로 만들지 않고 표시/숨김만 전환한다 */
    useEffect(() => {
        if (!ready || !mapRef.current) return;
        const map = mapRef.current;
        const visibleSet = visibleIds ? new Set(visibleIds) : null;
        Object.entries(layersRef.current).forEach(([id, layer]) => {
            const show = !visibleSet || visibleSet.has(id);
            layer.polygon.setMap(show ? map : null);
            layer.label.setMap(show ? map : null);
        });
    }, [visibleIds, zones, ready]);

    /* ── 그리기 모드 on/off ── */
    const clearDraw = useCallback(() => {
        drawMkrsRef.current.forEach(m => m?.setMap(null));
        drawMkrsRef.current = [];
        drawLineRef.current?.setMap(null);
        drawLineRef.current = null;
        drawPtsRef.current = [];
        setPtCount(0);
    }, []);

    const completeDraw = useCallback(() => {
        const pts = drawPtsRef.current;
        if (pts.length < 3 || !mapRef.current || !window.naver?.maps) return;
        const nm = window.naver.maps;
        if (drawEvtRef.current) {
            nm.Event.removeListener(drawEvtRef.current);
            drawEvtRef.current = null;
        }
        const captured = [...pts];
        clearDraw();
        mapRef.current.setCursor('');
        /* onDrawCompleteRef 콜백은 부모의 openPanel(패널 열기) 상태 변경을 일으키는데,
         * 첫 꼭지점을 다시 클릭해 완성하는 경로는 Naver 지도 SDK 자체의 네이티브 클릭
         * 이벤트 처리 도중(동일 이벤트가 아직 전파되는 중) 호출된다. 이 시점에 곧바로
         * 패널을 열면 그 네이티브 이벤트의 나머지 처리와 겹쳐 패널이 열리자마자
         * 닫히는 것처럼 보이는 경우가 있어, 다음 tick으로 미뤄 완전히 분리한다. */
        setTimeout(() => onDrawCompleteRef.current?.(captured), 0);
    }, [clearDraw]);

    useEffect(() => {
        if (!ready || !mapRef.current || !window.naver?.maps) return;
        const nm = window.naver.maps;

        if (!drawingMode) {
            if (drawEvtRef.current) {
                nm.Event.removeListener(drawEvtRef.current);
                drawEvtRef.current = null;
            }
            clearDraw();
            mapRef.current.setCursor('');
            return;
        }

        mapRef.current.setCursor('crosshair');
        drawEvtRef.current = nm.Event.addListener(
            mapRef.current,
            'click',
            (e: any) => {
                const lat = e.coord.lat(),
                    lng = e.coord.lng();
                const pts = drawPtsRef.current;

                if (
                    pts.length >= 3 &&
                    pxDist(mapRef.current, nm, pts[0], { lat, lng }) < 30
                ) {
                    completeDraw();
                    return;
                }

                pts.push({ lat, lng });
                const n = pts.length;
                setPtCount(n);

                if (n === 3 && drawMkrsRef.current[0]) {
                    drawMkrsRef.current[0].setIcon({
                        content: vtxHtml(true),
                        anchor: new nm.Point(9, 9),
                    });
                }

                const isFirst = n === 1;
                drawMkrsRef.current.push(
                    new nm.Marker({
                        position: new nm.LatLng(lat, lng),
                        map: mapRef.current,
                        icon: {
                            content: isFirst ? vtxHtml(false) : midVtxHtml(),
                            anchor: new nm.Point(
                                isFirst ? 9 : 6,
                                isFirst ? 9 : 6,
                            ),
                        },
                        zIndex: 999,
                        clickable: false,
                    }),
                );

                const path = pts.map(p => new nm.LatLng(p.lat, p.lng));
                if (drawLineRef.current) {
                    drawLineRef.current.setPath(path);
                } else if (path.length >= 2) {
                    drawLineRef.current = new nm.Polyline({
                        map: mapRef.current,
                        path,
                        strokeColor: '#1E8B53',
                        strokeWeight: 2.5,
                        strokeOpacity: 0.9,
                        zIndex: 998,
                    });
                }
            },
        );

        return () => {
            if (drawEvtRef.current) {
                nm.Event.removeListener(drawEvtRef.current);
                drawEvtRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawingMode, ready]);

    const switchType = (t: 'NORMAL' | 'SATELLITE') => {
        if (!mapRef.current || !window.naver?.maps) return;
        mapRef.current.setMapTypeId(window.naver.maps.MapTypeId[t]);
        setMapType(t);
    };

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
            {!ready && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 8,
                        background: 'var(--surface2)',
                        color: 'var(--text3)',
                        fontSize: 12.5,
                    }}
                >
                    <i className="ri-map-2-line" style={{ fontSize: 24 }} />
                    지도를 불러오는 중입니다...
                </div>
            )}
            {drawingMode && (
                <div
                    style={{
                        position: 'absolute',
                        top: 12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        background: 'rgba(30,139,83,0.94)',
                        color: '#fff',
                        borderRadius: 9,
                        padding: '8px 10px 8px 18px',
                        fontSize: 12.5,
                        fontWeight: 700,
                        fontFamily: 'Pretendard,-apple-system,sans-serif',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 4px 14px rgba(0,0,0,0.22)',
                    }}
                >
                    <span style={{ pointerEvents: 'none' }}>
                        {ptCount === 0
                            ? '지도를 클릭해 첫 번째 꼭지점을 찍으세요'
                            : ptCount < 3
                              ? `꼭지점 ${ptCount}개 · 최소 3개 이상 찍어주세요`
                              : `꼭지점 ${ptCount}개 · 첫 번째 꼭지점을 다시 클릭하거나 완성 버튼을 누르세요`}
                    </span>
                    {ptCount >= 3 && (
                        <button
                            onClick={completeDraw}
                            style={{
                                background: '#fff',
                                color: '#166840',
                                border: 'none',
                                borderRadius: 7,
                                padding: '5px 12px',
                                fontSize: 12,
                                fontWeight: 800,
                                cursor: 'pointer',
                                flexShrink: 0,
                            }}
                        >
                            ✓ 완성
                        </button>
                    )}
                </div>
            )}
            {mapTypeToggle && (
                <div
                    style={{
                        position: 'absolute',
                        right: 16,
                        zIndex: 1000,
                        display: 'flex',
                        ...(toggleAnchor === 'top-right'
                            ? { top: 16 }
                            : { bottom: 16 }),
                        borderRadius: 8,
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                        border: '1.5px solid #E2E8E2',
                    }}
                >
                    {(['NORMAL', 'SATELLITE'] as const).map(t => (
                        <button
                            key={t}
                            onClick={() => switchType(t)}
                            style={{
                                padding: '6px 14px',
                                fontSize: 12,
                                fontWeight: 700,
                                fontFamily:
                                    'Pretendard,-apple-system,sans-serif',
                                border: 'none',
                                cursor: 'pointer',
                                background: mapType === t ? '#1E8B53' : '#fff',
                                color: mapType === t ? '#fff' : '#4A6B4A',
                            }}
                        >
                            {t === 'NORMAL' ? '일반' : '위성'}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
